import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface ThemeStore {
  theme: 'light' | 'dark';
  isAdmin: boolean;
  toggleTheme: () => void;
  setTheme: (theme: 'light' | 'dark') => void;
  setIsAdmin: (isAdmin: boolean) => void;
  applyTheme: () => void;
}

// Helper to apply theme to DOM
const applyThemeToDOM = (theme: 'light' | 'dark') => {
  document.documentElement.setAttribute('data-theme', theme);
  // Also set class for any components that might use it
  document.documentElement.classList.remove('light', 'dark');
  document.documentElement.classList.add(theme);
};

export const useThemeStore = create<ThemeStore>()(
  persist(
    (set, get) => ({
      theme: 'dark',
      isAdmin: false,
      toggleTheme: () => {
        const newTheme = get().theme === 'dark' ? 'light' : 'dark';
        applyThemeToDOM(newTheme);
        set({ theme: newTheme });
      },
      setTheme: (theme: 'light' | 'dark') => {
        applyThemeToDOM(theme);
        set({ theme });
      },
      setIsAdmin: (isAdmin: boolean) => set({ isAdmin }),
      applyTheme: () => {
        applyThemeToDOM(get().theme);
      },
    }),
    {
      name: 'theme-storage',
      onRehydrateStorage: () => (state) => {
        // Apply theme when store is rehydrated from localStorage
        if (state) {
          applyThemeToDOM(state.theme);
        }
      },
    }
  )
);

export const getCurrentTheme = (state: ThemeStore) => state.theme;